import { createStore } from '@/lib/tinyStore';
import type { NotificationItem } from './types';

export interface MessagePopupRow {
  id: string;
  actorId?: string;
  name: string;
  avatarUrl?: string;
  preview: string;
  createdAt: number;
  deepLink?: string;
  unread: boolean;
  /** Epoch ms at which this row auto-dismisses. */
  expiresAt: number;
}

export interface PopupState {
  rows: readonly MessagePopupRow[];
  /** Hover/focus freezes every countdown — the card must not vanish mid-read. */
  paused: boolean;
}

/** The reference card shows three rows; older ones roll off the bottom. */
const MAX_ROWS = 3;
const DISMISS_MS = 7000;

export const popupStore = createStore<PopupState>({ rows: [], paused: false });

export const popupActions = {
  push(item: NotificationItem, now = Date.now()): void {
    const row: MessagePopupRow = {
      id: item.id,
      actorId: item.actorId,
      name: item.preview.actorName ?? item.title,
      avatarUrl: item.preview.actorAvatarUrl,
      preview: item.preview.snippet ?? item.body ?? '',
      createdAt: item.createdAt,
      deepLink: item.deepLink,
      unread: !item.isRead,
      expiresAt: now + DISMISS_MS,
    };

    popupStore.set((state) => ({
      ...state,
      rows: [row, ...state.rows.filter((existing) => existing.id !== row.id)].slice(0, MAX_ROWS),
    }));
  },

  dismiss(id: string): void {
    popupStore.set((state) => {
      const rows = state.rows.filter((row) => row.id !== id);
      return rows.length === state.rows.length ? state : { ...state, rows };
    });
  },

  dismissAll(): void {
    popupStore.set((state) => (state.rows.length === 0 ? state : { ...state, rows: [] }));
  },

  setPaused(paused: boolean, now = Date.now()): void {
    popupStore.set((state) => {
      if (state.paused === paused) return state;
      if (paused) return { ...state, paused };
      /* Resuming: give every row a fresh full window so nothing pops instantly. */
      return { paused, rows: state.rows.map((row) => ({ ...row, expiresAt: now + DISMISS_MS })) };
    });
  },

  /** Called once a second only while the card is on screen. */
  sweep(now = Date.now()): void {
    const state = popupStore.get();
    if (state.paused || state.rows.length === 0) return;
    const rows = state.rows.filter((row) => row.expiresAt > now);
    if (rows.length !== state.rows.length) popupStore.set({ ...state, rows });
  },
};

export const selectPopupRows = (state: PopupState): readonly MessagePopupRow[] => state.rows;
